import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Pause, Volume2, VolumeX, SkipForward, SkipBack } from 'lucide-react';
import { useMusic } from '../context/MusicContext';

/**
 * Global music mini-player — renders fixed at the top of every page.
 * Compact bar sits below any existing page nav (top: 4rem offset on routes with their own nav,
 * or directly at the top on routes without one).
 */
export const GlobalMusicBar = () => {
  const navigate = useNavigate();
  const { playlist, currentTrack, isPlaying, isMuted, volume, playPause, next, prev, setIsMuted, setVolume } = useMusic();

  if (playlist.length === 0 || !currentTrack) return null;

  return (
    <>
      {/* Spacer so fixed bar doesn't cover page footer/content */}
      <div aria-hidden="true" className="h-14 sm:h-16 w-full" />
      <div
        data-testid="global-music-bar"
        className="fixed bottom-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-xl border-t border-amber-500/30"
        style={{ boxShadow: '0 -8px 32px rgba(0,0,0,0.6)' }}
      >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6">
        <div className="flex items-center gap-2 sm:gap-3 py-2 sm:py-2.5">
          {/* Track label — clickable → homepage playlist */}
          <button
            onClick={() => {
              navigate('/');
              setTimeout(() => {
                document.getElementById('music')?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
            data-testid="global-track-btn"
            className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 text-left group"
          >
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-md bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {currentTrack.thumbnail_url ? (
                <img src={currentTrack.thumbnail_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 24 24"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-semibold truncate text-white group-hover:text-amber-400 transition-colors">
                {currentTrack.title}
              </p>
              <p className="text-[9px] sm:text-[10px] text-zinc-500 truncate uppercase tracking-wider">
                {isPlaying ? (isMuted ? '● muted' : '● playing') : '○ paused'} · Official Playlist
              </p>
            </div>
          </button>

          {/* Prev / Play / Next */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={prev}
              aria-label="Previous"
              data-testid="global-prev-btn"
              className="hidden sm:flex w-8 h-8 rounded-full hover:bg-zinc-800 text-zinc-400 hover:text-white items-center justify-center transition-colors"
            >
              <SkipBack className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={playPause}
              aria-label={isPlaying ? 'Pause' : 'Play'}
              data-testid="global-playpause-btn"
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-amber-500 hover:bg-amber-400 text-black flex items-center justify-center hover:scale-105 transition-all"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </button>
            <button
              onClick={next}
              aria-label="Next"
              data-testid="global-next-btn"
              className="hidden sm:flex w-8 h-8 rounded-full hover:bg-zinc-800 text-zinc-400 hover:text-white items-center justify-center transition-colors"
            >
              <SkipForward className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Mute + Volume */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setIsMuted(!isMuted)}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
              data-testid="global-mute-btn"
              className="w-8 h-8 rounded-full hover:bg-zinc-800 text-zinc-400 hover:text-white flex items-center justify-center transition-colors"
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={e => setVolume(parseFloat(e.target.value))}
              data-testid="global-volume-slider"
              aria-label="Volume"
              className="hidden md:block w-16 lg:w-24 h-1 accent-amber-500 cursor-pointer"
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};
